import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, Play, Volume2 } from "lucide-react";
import { fetchVoiceOptions, saveVoicePreference, synthesizeSpeech, triggerEveningReturn } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import VoiceCloneCard from "@/components/VoiceCloneCard";
import PauseCountdownCard from "@/components/PauseCountdownCard";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [voices, setVoices] = useState([]);
  const [defaultVoice, setDefaultVoice] = useState("fable");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingVoiceId, setSavingVoiceId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [sendingEvening, setSendingEvening] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchVoiceOptions()
      .then((data) => {
        setVoices(data.voices || []);
        setDefaultVoice(data.default || "fable");
      })
      .catch(() => toast.error("Could not load voices."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelected(user?.preferred_voice || null);
  }, [user]);

  const activeVoiceId = selected || defaultVoice;

  const previewVoice = async (voiceId, sample) => {
    if (previewingId) return;
    // Stop any currently-playing preview
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPreviewingId(voiceId);
    try {
      const url = await synthesizeSpeech(sample, voiceId);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setPreviewingId(null);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setPreviewingId(null);
        URL.revokeObjectURL(url);
        toast.error("Could not play this preview.");
      };
      await audio.play();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("The voice is quiet right now.");
      }
      setPreviewingId(null);
    }
  };

  const chooseVoice = async (voiceId) => {
    if (saving) return;
    setSaving(true);
    setSavingVoiceId(voiceId);
    try {
      await saveVoicePreference(voiceId);
      setSelected(voiceId);
      await refresh();
      toast.success("Voice saved. Every future reflection will use it.");
    } catch (e) {
      console.error(e);
      toast.error("Could not save your voice choice.");
    } finally {
      setSaving(false);
      setSavingVoiceId(null);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian-900 text-bone-200">
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-12 pb-24">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 min-h-[44px] px-2 -mx-2 py-2 sm:min-h-0 sm:px-0 sm:mx-0 font-sans text-xs uppercase tracking-[0.24em] text-bone-500 hover:text-bone-200 mb-6 sm:mb-8"
          data-testid="settings-back-link"
        >
          <ArrowLeft className="h-3 w-3" /> Back to the workbook
        </Link>

        <header className="mb-14">
          <p className="font-sans uppercase tracking-[0.28em] text-[10px] text-terracotta mb-4">
            Umbra · Settings
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-bone-100 mb-4" data-testid="settings-title">
            The voice that reads you back
          </h1>
          <p className="font-serif italic text-lg text-bone-400 max-w-2xl leading-relaxed">
            Choose the narrator that reads your prompts aloud. Reflections are read back by a
            <span className="text-terracotta not-italic"> paired voice </span>
            for gentle contrast — like another presence answering.
          </p>
        </header>

        <PauseCountdownCard />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="voice-picker-grid">
          {voices.map((v) => {
            const isActive = activeVoiceId === v.id;
            const isPreviewing = previewingId === v.id;
            return (
              <div
                key={v.id}
                data-testid={`voice-card-${v.id}`}
                className={`border p-6 transition-colors ${
                  isActive
                    ? "border-terracotta bg-terracotta/5"
                    : "border-obsidian-600 hover:border-bone-500"
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h2 className="font-serif text-2xl text-bone-100 mb-1" data-testid={`voice-name-${v.id}`}>
                      {v.label}
                    </h2>
                    <p className="font-sans text-[10px] uppercase tracking-[0.24em] text-terracotta">
                      {v.tone}
                    </p>
                  </div>
                  {isActive && (
                    <span
                      className="inline-flex items-center gap-1 border border-terracotta bg-terracotta/10 px-2 py-1 font-sans text-[10px] uppercase tracking-[0.24em] text-terracotta"
                      data-testid={`voice-active-${v.id}`}
                    >
                      <Check className="h-3 w-3" /> Current
                    </span>
                  )}
                </div>

                <p className="font-serif italic text-bone-400 text-sm leading-relaxed mb-5 min-h-[3rem]">
                  "{v.sample}"
                </p>
                {v.available === false && (
                  <p className="-mt-3 mb-4 font-sans text-xs text-bone-500" data-testid={`voice-paused-${v.id}`}>
                    The founder's voice is paused for a moment. Lines play in a stand-in voice until it's back.
                  </p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => previewVoice(v.id, v.sample)}
                    disabled={previewingId !== null || v.available === false}
                    data-testid={`voice-preview-${v.id}`}
                    className="inline-flex items-center gap-2 border border-obsidian-600 hover:border-terracotta text-bone-300 hover:text-terracotta px-4 py-2 font-sans text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isPreviewing ? (
                      <>
                        <Volume2 className="h-4 w-4 animate-pulse" /> Playing…
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" /> Preview
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => chooseVoice(v.id)}
                    disabled={saving || isActive || v.available === false}
                    data-testid={`voice-choose-${v.id}`}
                    className={`inline-flex items-center gap-2 border px-4 py-2 font-sans text-xs uppercase tracking-[0.2em] transition-colors disabled:cursor-not-allowed ${
                      isActive
                        ? "border-obsidian-600 text-bone-500"
                        : "border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian-900"
                    }`}
                  >
                    {(() => {
                      if (savingVoiceId === v.id) return <Loader2 className="h-4 w-4 animate-spin" />;
                      if (isActive) return "Chosen";
                      return "Choose";
                    })()}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="font-sans text-xs text-bone-500 mt-10 text-center" data-testid="settings-footer-note">
          Your choice is remembered on this account. You can change it anytime.
        </p>

        <div className="mt-16 border-t border-obsidian-600 pt-12">
          <p className="font-sans uppercase tracking-[0.28em] text-[10px] text-terracotta mb-4">
            Umbra · Your voice
          </p>
          <h2 className="font-serif text-3xl text-bone-100 mb-4" data-testid="settings-voice-clone-title">
            Narrate every entry in your own voice
          </h2>
          <p className="font-serif italic text-bone-400 max-w-2xl leading-relaxed mb-6">
            Clone your speaking voice once — then hear every future journal entry
            read back in it. The most intimate way to sit with what you've said.
          </p>
          <VoiceCloneCard />
        </div>

        <div className="mt-16 border-t border-obsidian-600 pt-12">
          <p className="font-sans uppercase tracking-[0.28em] text-[10px] text-terracotta mb-4">
            Umbra · Evening Return
          </p>
          <h2 className="font-serif text-3xl text-bone-100 mb-4" data-testid="settings-evening-title">
            Come hear your own words back
          </h2>
          <p className="font-serif italic text-bone-400 max-w-2xl leading-relaxed mb-6">
            A quiet email at dusk, inviting you back to hear your Mirror. Tap below to send yourself
            a preview so you know what to expect.
          </p>
          <button
            data-testid="evening-return-preview-btn"
            disabled={sendingEvening}
            onClick={async () => {
              setSendingEvening(true);
              try {
                const r = await triggerEveningReturn();
                toast.success(`Sent to ${r?.sent_to || "your inbox"}.`);
              } catch (e) {
                const detail = e?.response?.data?.detail;
                toast.error(detail || "Could not send the evening return.");
              } finally {
                setSendingEvening(false);
              }
            }}
            className="inline-flex items-center gap-2 border border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian-900 px-5 py-3 font-sans text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendingEvening ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
            Send me a preview
          </button>
        </div>
      </div>
    </div>
  );
}
